import {
  CalendarDetailsVisibility,
  ID,
  LinkedItemType,
  SyncMode,
} from '@griply/types';
import { canConnectionWriteEvents, resolveSyncTasksFrom } from '@griply/utils';
import {
  useCalendarSyncs,
  useExternalCalendarConnections,
  useRemoveCalendarSync,
  useSetCalendarSync,
  useTwoWaySyncAllowed,
} from 'features/external-connections';
import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'shared/assets/icons';
import { Divider } from 'shared/components/ui/divider';
import { Icon } from 'shared/components/ui/icon';
import { useExternalConnectionsCalendarsQueries } from 'shared/hooks/queries/use-external-connections-calendars-queries';

import { CalendarReconnectNotice } from './calendar-reconnect-notice';
import { CalendarSelectValue } from './calendar-select';
import { CalendarSyncOptionRow } from './calendar-sync-option-row';
import * as Styled from './calendar-sync-options.style';

type DraftRow = {
  key: string;
  linkedItemId: ID | null;
  linkedItemType: LinkedItemType | null;
  calendar: CalendarSelectValue | null;
  mode: SyncMode | null;
  detailsVisibility: CalendarDetailsVisibility | null;
};

const emptyDraft = (key: string): DraftRow => ({
  key,
  linkedItemId: null,
  linkedItemType: null,
  calendar: null,
  mode: SyncMode.Automatic,
  detailsVisibility: CalendarDetailsVisibility.Visible,
});

export const CalendarSyncOptionsList: React.FC = () => {
  const { t } = useTranslation();
  const connections = useExternalCalendarConnections();
  const syncs = useCalendarSyncs();
  const goals = useGoals();
  const lifeAreas = useLocalizedLifeAreas();
  const setCalendarSync = useSetCalendarSync();
  const removeCalendarSync = useRemoveCalendarSync();
  const { allowed } = useTwoWaySyncAllowed();

  // Query every connection, including non-write-capable ones: getCalendars re-derives
  // capabilities from the provider's actually-granted scope and writes them back, so this call
  // is what heals a connection whose stored capabilities are missing or stale.
  const connectionIds = useMemo(
    () => connections.map(({ id }) => id),
    [connections],
  );
  const connectionsNeedingReconnect = useMemo(
    () =>
      connections.filter((connection) => !canConnectionWriteEvents(connection)),
    [connections],
  );
  const { calendars } = useExternalConnectionsCalendarsQueries(connectionIds);

  // Only a write-capable connection can host a task sync, so its calendars are the only ones
  // offered — but every calendar stays available for resolving an already-configured row.
  const selectableCalendars = useMemo(() => {
    const syncableIds = connections
      .filter(canConnectionWriteEvents)
      .map(({ id }) => id);
    return calendars.filter(({ connectionId }) =>
      syncableIds.includes(connectionId),
    );
  }, [calendars, connections]);

  const draftKey = useRef(0);
  const [drafts, setDrafts] = useState<DraftRow[]>([]);

  const updateDraft = (key: string, changes: Partial<DraftRow>) => {
    setDrafts((current) =>
      current.flatMap((draft) => {
        if (draft.key !== key) {
          return [draft];
        }
        const next = { ...draft, ...changes };
        if (
          next.linkedItemId &&
          next.linkedItemType &&
          next.calendar &&
          next.mode
        ) {
          setCalendarSync({
            connectionId: next.calendar.connectionId,
            linkedItemId: next.linkedItemId,
            linkedItemType: next.linkedItemType,
            calendarId: next.calendar.calendarId,
            mode: next.mode,
            detailsVisibility: next.detailsVisibility ?? undefined,
            syncTasksFrom: resolveSyncTasksFrom(
              next.linkedItemType,
              undefined,
              new Date(),
            ),
          });
          return [];
        }
        return [next];
      }),
    );
  };

  const removeDraft = (key: string) =>
    setDrafts((current) => current.filter((draft) => draft.key !== key));

  const onAddRow = () => {
    draftKey.current += 1;
    setDrafts((current) => [
      ...current,
      emptyDraft(`draft-${draftKey.current}`),
    ]);
  };

  if (!connections.length) {
    return null;
  }

  return (
    <Styled.Section>
      <Divider />

      <Styled.Title>
        {t('forms.user-settings.sections.calendar.sync-tasks.title')}
      </Styled.Title>

      <CalendarReconnectNotice connections={connectionsNeedingReconnect} />

      <Styled.Table>
        <Styled.HeaderRow>
          <Styled.ColumnLabel>
            {t(
              'forms.user-settings.sections.calendar.sync-tasks.columns.tasks',
            )}
          </Styled.ColumnLabel>
          <span />
          <Styled.ColumnLabel>
            {t(
              'forms.user-settings.sections.calendar.sync-tasks.columns.calendar',
            )}
          </Styled.ColumnLabel>
          <Styled.ColumnLabel>
            {t(
              'forms.user-settings.sections.calendar.sync-tasks.columns.task-sync',
            )}
          </Styled.ColumnLabel>
          <Styled.ColumnLabel>
            {t(
              'forms.user-settings.sections.calendar.sync-tasks.columns.task-details',
            )}
          </Styled.ColumnLabel>
          <span />
        </Styled.HeaderRow>

        {syncs.map((sync) => {
          const onChangeLinkedItem = (
            link: { id: ID; type: LinkedItemType } | null,
          ) => {
            removeCalendarSync({
              connectionId: sync.connectionId,
              linkedItemId: sync.linkedItemId,
            });
            if (link) {
              setCalendarSync({
                connectionId: sync.connectionId,
                linkedItemId: link.id,
                linkedItemType: link.type,
                calendarId: sync.calendarId,
                mode: sync.mode,
                detailsVisibility: sync.detailsVisibility,
                syncTasksFrom: resolveSyncTasksFrom(
                  link.type,
                  sync.syncTasksFrom,
                  new Date(),
                ),
              });
            }
          };
          const onChangeCalendar = (value: CalendarSelectValue) => {
            if (value.connectionId !== sync.connectionId) {
              removeCalendarSync({
                connectionId: sync.connectionId,
                linkedItemId: sync.linkedItemId,
              });
            }
            setCalendarSync({
              connectionId: value.connectionId,
              linkedItemId: sync.linkedItemId,
              linkedItemType: sync.linkedItemType,
              calendarId: value.calendarId,
              mode: sync.mode,
              detailsVisibility: sync.detailsVisibility,
              syncTasksFrom: resolveSyncTasksFrom(
                sync.linkedItemType,
                sync.syncTasksFrom,
                new Date(),
              ),
            });
          };
          const onChangeMode = (mode: SyncMode) =>
            setCalendarSync({
              connectionId: sync.connectionId,
              linkedItemId: sync.linkedItemId,
              linkedItemType: sync.linkedItemType,
              calendarId: sync.calendarId,
              mode,
              detailsVisibility: sync.detailsVisibility,
              syncTasksFrom: resolveSyncTasksFrom(
                sync.linkedItemType,
                sync.syncTasksFrom,
                new Date(),
              ),
            });
          const onChangeVisibility = (
            detailsVisibility: CalendarDetailsVisibility,
          ) =>
            setCalendarSync({
              connectionId: sync.connectionId,
              linkedItemId: sync.linkedItemId,
              linkedItemType: sync.linkedItemType,
              calendarId: sync.calendarId,
              mode: sync.mode,
              detailsVisibility,
              syncTasksFrom: resolveSyncTasksFrom(
                sync.linkedItemType,
                sync.syncTasksFrom,
                new Date(),
              ),
            });
          const onRemove = () =>
            removeCalendarSync({
              connectionId: sync.connectionId,
              linkedItemId: sync.linkedItemId,
            });

          return (
            <CalendarSyncOptionRow
              key={`${sync.connectionId}:${sync.linkedItemId}`}
              linkedItemId={sync.linkedItemId}
              calendar={{
                connectionId: sync.connectionId,
                calendarId: sync.calendarId,
              }}
              mode={sync.mode}
              detailsVisibility={sync.detailsVisibility ?? null}
              goals={goals}
              lifeAreas={lifeAreas}
              calendars={calendars}
              selectableCalendars={selectableCalendars}
              onChangeLinkedItem={onChangeLinkedItem}
              onChangeCalendar={onChangeCalendar}
              onChangeMode={onChangeMode}
              onChangeVisibility={onChangeVisibility}
              onRemove={onRemove}
            />
          );
        })}

        {drafts.map((draft) => (
          <CalendarSyncOptionRow
            key={draft.key}
            linkedItemId={draft.linkedItemId}
            calendar={draft.calendar}
            mode={draft.mode}
            detailsVisibility={draft.detailsVisibility}
            goals={goals}
            lifeAreas={lifeAreas}
            calendars={calendars}
            selectableCalendars={selectableCalendars}
            onChangeLinkedItem={(link) =>
              updateDraft(draft.key, {
                linkedItemId: link?.id ?? null,
                linkedItemType: link?.type ?? null,
              })
            }
            onChangeCalendar={(calendar) =>
              updateDraft(draft.key, { calendar })
            }
            onChangeMode={(mode) => updateDraft(draft.key, { mode })}
            onChangeVisibility={(detailsVisibility) =>
              updateDraft(draft.key, { detailsVisibility })
            }
            onRemove={() => removeDraft(draft.key)}
          />
        ))}
      </Styled.Table>

      {allowed ? (
        <Styled.AddButton type="button" onClick={onAddRow}>
          <Styled.AddIconContainer>
            <Icon icon={Plus} />
          </Styled.AddIconContainer>
          {t('forms.user-settings.sections.calendar.sync-tasks.add')}
        </Styled.AddButton>
      ) : (
        <Styled.Upsell>
          {t('forms.user-settings.sections.calendar.sync-tasks.upsell')}
        </Styled.Upsell>
      )}
    </Styled.Section>
  );
};
